import { providerDisplayName } from '../lib/providerDisplayName.js';
import { statusPageUrl } from '../lib/statusPageUrl.js';
import ExternalIndicator from './ExternalIndicator.js';
import ExternalStatusIcon from './ExternalStatusIcon.js';
import ProviderInfoTooltip from './ProviderInfoTooltip.js';
import TierBadge from './TierBadge.js';
import StatusGlow from './ui/StatusGlow.js';

// ProviderRow v0.7.1 — shared row layout moved to command-system visuals.
// Mini sparklines were removed because latency/history now has a dedicated
// page and the dense provider rows need cleaner readability.
interface Props {
  name: string;
  tier: number;
  severity: number;
  sourceUrl: string;
  // Optional override for the click-through URL — see statusPageUrl helper.
  dashboardUrl?: string | null;
  message?: string;
  recentSeverities?: number[];
  external?: boolean;
  description?: string;
  // Letter grade ('A+'..'D-') when set. F-graded providers don't render
  // here at all (they're routed to TierFCard), so this is purely a small
  // chrome cue for the "we have a grade" case.
  reputationGrade?: string | null;
}

export default function ProviderRow({
  name,
  tier,
  severity,
  sourceUrl,
  dashboardUrl,
  message,
  recentSeverities = [],
  external = false,
  description,
  reputationGrade,
}: Props) {
  const href = statusPageUrl({ source_url: sourceUrl, dashboard_url: dashboardUrl });
  const severityIdx = Math.max(0, Math.min(3, Math.floor(severity)));
  const severityLabel = ['OK', 'DEG', 'MAJ', 'DOWN'][severityIdx];
  const severityTone = severityIdx as 0 | 1 | 2 | 3;
  const isMajor = severityTone === 2;
  const displayName = providerDisplayName('', name);
  void recentSeverities;

  return (
    <div
      className={`grid items-center gap-3 px-3 py-2 border-b border-border/45 text-sm font-mono hover:bg-surface2 min-w-0 md:text-[13px] ${
        isMajor ? 'bg-bad/20' : ''
      }`}
      style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(86px, auto) 38px' }}
      title={message ? `${name} — ${message}` : name}
    >
      <span className="min-w-0 flex items-center gap-1">
        <span className="text-white font-medium truncate">{displayName}</span>
        <TierBadge tier={tier} />
        {reputationGrade && reputationGrade !== 'F' && (
          <span
            className="ml-1 px-1 text-[10px] font-mono text-white border border-border rounded-sm"
            title={`Reputation grade: ${reputationGrade}`}
          >
            {reputationGrade}
          </span>
        )}
        <span className="text-xs text-white">T{tier}</span>
        {description && <ProviderInfoTooltip description={description} />}
      </span>
      {external ? (
        <span className="text-accent text-[10px] uppercase tracking-[1.5px]">
          <ExternalIndicator href={href} label={name} />
        </span>
      ) : (
        <StatusGlow severity={severityTone}>{severityLabel}</StatusGlow>
      )}
      <ExternalStatusIcon href={href} label={`Open ${name} status page`} />
    </div>
  );
}
